import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, FileCheck2, FlaskConical, ArrowRight } from "lucide-react";

// Dark-navy trust/verification band. Round 2 revision (2026-02-14): the
// three illustrative lot cards were pulled, they read as unscrubbed
// test data rather than curated content. In their place we surface the
// verify code input directly, so the tool IS the content. The three
// trust points below stay as-is.
export default function TrustVerificationBand() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    // Hand off to /verify with the code prefilled. VerifyPage reads the
    // ?code= query param and auto-runs the lookup.
    navigate(`/verify?code=${encodeURIComponent(trimmed)}`);
  };

  return (
    <section
      className="border-b border-white/5 bg-navy text-white"
      data-testid="trust-verification-band"
    >
      <div className="container-pd py-16 lg:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-6">
            <p className="label-mono !text-brandbluelight">§ Live lot ledger</p>
            <h2 className="mt-3 font-heading text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl">
              Every box carries a code.
              <br />
              <span className="text-brandbluelight">Every code traces a lot.</span>
            </h2>
            <p className="mt-5 max-w-md text-sm text-white/60">
              Enter the 8-character code on the inside flap of your box. We&apos;ll
              surface the lot number, first-verified timestamp, and every check
              logged against it.
            </p>
          </div>

          {/* Inline verify tool, front and centre. Submitting hands off to
              /verify with ?code= prefilled so the lookup runs immediately. */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-6"
            data-testid="trust-verify-form"
          >
            <label
              htmlFor="trust-verify-code"
              className="block font-mono text-[10px] tracking-[0.2em] text-brandbluelight"
            >
              VERIFY A BOX
            </label>
            <div className="mt-3 flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-2 sm:flex-row sm:items-center">
              <input
                id="trust-verify-code"
                type="text"
                inputMode="latin"
                maxLength={12}
                autoComplete="off"
                spellCheck={false}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. TM-XXXX-XX"
                className="flex-1 rounded-lg bg-transparent px-4 py-3 font-mono text-base tracking-[0.15em] text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brandbluelight/40"
                data-testid="trust-verify-input"
              />
              <button
                type="submit"
                disabled={!code.trim()}
                className="btn-pill inline-flex items-center justify-center gap-2 bg-brandbluelight px-6 py-3 text-sm font-bold text-navy transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="trust-verify-submit"
              >
                Verify <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
              </button>
            </div>
            <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-white/40">
              8-CHARACTER · CASE-INSENSITIVE · ONE CHECK PER CODE PER 24H
            </p>
          </form>
        </div>

        <ul className="mt-14 grid grid-cols-1 gap-6 border-t border-white/10 pt-8 sm:grid-cols-3">
          <TrustPoint
            icon={FlaskConical}
            title="HPLC ≥ 99.5%"
            body="Independently tested by Janoshik Analytical. Every lot, no exceptions."
          />
          <TrustPoint
            icon={FileCheck2}
            title="COAs published"
            body="Two PDFs per lot (HPLC + pH) live on the product page before checkout."
          />
          <TrustPoint
            icon={ShieldCheck}
            title="Anti-counterfeit"
            body="Unique code inside each box. Flag suspicious repeats, we blacklist within 24h."
          />
        </ul>
      </div>
    </section>
  );
}

function TrustPoint({ icon: Icon, title, body }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brandblue/15 text-brandbluelight">
        <Icon className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0">
        <p className="font-heading text-sm font-bold">{title}</p>
        <p className="mt-1 text-xs leading-relaxed text-white/60">{body}</p>
      </div>
    </li>
  );
}
